import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Helper function to convert ArrayBuffer to base64 string
 * More robust than btoa() for binary data
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Downloads an Excel file with platform-specific handling
 * - Web: Uses browser download
 * - Android: Saves to device storage
 */
export const downloadExcel = async (
  data: any[],
  fileName: string,
  sheetName: string = 'Sheet1',
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Android/iOS: Save to device storage
      await downloadExcelNative(data, fileName, sheetName, onSuccess, onError);
    } else {
      // Web: Use browser download
      await downloadExcelWeb(data, fileName, sheetName, onSuccess, onError);
    }
  } catch (error) {
    console.error('Excel download error:', error);
    onError?.(error instanceof Error ? error.message : 'Download failed');
  }
};

/**
 * Shares an Excel file with platform-specific handling
 * - Web: Opens in new tab (if supported)
 * - Android: Opens native share dialog
 */
export const shareExcel = async (
  data: any[],
  fileName: string,
  sheetName: string = 'Sheet1',
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Android/iOS: Save to device and share
      await shareExcelNative(data, fileName, sheetName, onSuccess, onError);
    } else {
      // Web: Download (since Excel can't be opened in browser)
      await downloadExcelWeb(data, fileName, sheetName, onSuccess, onError);
    }
  } catch (error) {
    console.error('Excel share error:', error);
    onError?.(error instanceof Error ? error.message : 'Share failed');
  }
};

/**
 * Downloads Excel on native platforms (Android/iOS) - saves to device only
 */
const downloadExcelNative = async (
  data: any[],
  fileName: string,
  sheetName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    // Create Excel workbook
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate Excel file as array buffer
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Convert to base64 - use a more robust method for binary data
    const excelBase64 = arrayBufferToBase64(wbout);
    
    // Ensure fileName has .xlsx extension
    const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

    // Save to device storage
    await Filesystem.writeFile({
      path: finalFileName,
      data: excelBase64,
      directory: Directory.Documents,
      recursive: true
    });

    onSuccess?.();
  } catch (error) {
    console.error('Native Excel download error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to save Excel to device');
  }
};

/**
 * Shares Excel on native platforms (Android/iOS) - saves and opens share dialog
 */
const shareExcelNative = async (
  data: any[],
  fileName: string,
  sheetName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    // Create Excel workbook
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate Excel file as array buffer
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Convert to base64 - use a more robust method for binary data
    const excelBase64 = arrayBufferToBase64(wbout);
    
    // Ensure fileName has .xlsx extension
    const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;

    // Save to device storage
    const savedFile = await Filesystem.writeFile({
      path: finalFileName,
      data: excelBase64,
      directory: Directory.Documents,
      recursive: true
    });

    // Share the file
    await Share.share({
      title: 'Excel Export',
      text: `Excel file: ${fileName}`,
      url: savedFile.uri,
      dialogTitle: 'Share Excel File'
    });

    onSuccess?.();
  } catch (error) {
    console.error('Native Excel share error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to share Excel');
  }
};

/**
 * Downloads Excel on web platforms
 */
const downloadExcelWeb = async (
  data: any[],
  fileName: string,
  sheetName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    // Create Excel workbook
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generate Excel file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    
    // Download using file-saver
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 
           fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);

    onSuccess?.();
  } catch (error) {
    console.error('Web Excel download error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to download Excel');
  }
};
